<!-- eslint-disable vue/no-multiple-template-root -->
<template>
  <transition name="slide">
    <product-tabs-bar v-show="isSectionTabsShown" v-model="activatedTab" class="z-10" :sections="sections" @tab-change="handleTabChange" />
  </transition>

  <div id="product-page" class="bg-gray-20 pb-6 sm:pt-6 sm:pb-10 text-base">
    <div class="container mx-auto">
      <div class="flex justify-center relative gap-6">
        <div class="product-info w-full flex flex-col gap-2 md:gap-6 overflow-auto">
          <template v-for="section in sections" :key="section.id">
            <div
              v-if="section.show && section.component"
              :id="section.id"
              class="section-wrapper scroll-mt-36"
              :class="section.label ? topTabsClassName : ''"
            >
              <component :is="section.component" v-bind="section.props" v-on="section.events || {}" />
            </div>
          </template>
        </div>

        <product-sidebar
          v-if="productInfo && app.isDesktopScreen"
          v-model:bookingItem="bookingItem"
          v-model:additions="additions"
          v-model:upgrades="upgrades"
          :class="isSectionTabsShown ? 'max-h-[calc(100dvh-166px)] top-152px' : 'max-h-[calc(100dvh-112px)] top-22'"
          :product="productInfo"
          :product-comments="productComments?.data"
        />
      </div>
      <product-section-related-products
        v-if="sameBranchOtherProductIds?.length"
        id="product-detail-section-related-products"
        class="mt-6"
        :product="productInfo"
        :productIds="sameBranchOtherProductIds"
      />
    </div>
  </div>

  <product-mobile-footer-bar
    v-if="productInfo && (app.isMobile || app.isTablet)"
    class="z-10"
    :product="productInfo"
    :booking-item="bookingItem"
    :additions="additions"
    :upgrades="upgrades"
  />
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { MaybeElement } from '@vueuse/core'

import { getProducts, getProductInfo, getProductComments } from '@/service/product'

import { useAppStore } from '@/store/app'
import { useBookingStore } from '@/store/booking'
import { useFeatureToggleStore } from '@/store/feature-toggle'
import { useProductStore } from '@/store/product'

import ProductSidebar from '@/components/product/product-detail/product-sidebar.vue'
import ProductMobileFooterBar from '@/components/product/product-detail/product-mobile-footer-bar.vue'
import ProductTabsBar from '@/components/product/product-detail/product-tabs-bar.vue'

import ProductSectionGallery from '@/components/product/product-detail/product-section-gallery.vue'
import ProductSectionName from '@/components/product/product-detail/product-section-name.vue'
import ProductSectionBasicInfo from '@/components/product/product-detail/product-section-basic-info.vue'
import ProductSectionIntro from '@/components/product/product-detail/product-section-intro.vue'
import ProductSectionPriceList from '@/components/product/product-detail/product-section-price-list.vue'
import ProductSectionNeedToKnow from '@/components/product/product-detail/product-section-need-to-know.vue'
import ProductSectionComments from '@/components/product/product-detail/product-section-comments.vue'
import ProductSectionBranchInfo from '@/components/product/product-detail/product-section-branch-info.vue'
import ProductSectionAddress from '@/components/product/product-detail/product-section-address.vue'
import ProductSectionNotices from '@/components/product/product-detail/product-section-notices.vue'
import ProductSectionFaq from '@/components/product/product-detail/product-section-faq.vue'
import ProductSectionRelatedTags from '@/components/product/product-detail/product-section-related-tags.vue'
import ProductSectionRelatedProducts from '@/components/product/product-detail/product-section-related-products.vue'
import ProductSectionReservation from '@/components/product/product-detail/product-section-reservation.vue'

import { SortRuleEnum } from '@/types/product'
import type { UiProductInfo, UiProductSelectedData } from '@/types/product'
import type { ProductUpgradeResource, ProductAdditionsResource } from '@/types/booking'
import { GtmEvent } from '@/types/gtm'

import { DEFAULT_LANG } from '@/i18n.config'
import { ASYNC_DATA_KEYS, getAsyncDataKey } from '@/constants/async-data-keys'

definePageMeta({
  middleware: ['check-region'],
})

const { t, localeProperties } = useI18n()

const route = useRoute()
const productId = computed(() => route.params.pid as string)

const app = useAppStore()
const featureToggleStore = useFeatureToggleStore()
const productStore = useProductStore()

const { product: bookingProduct, bookingItem, additions, upgrades } = storeToRefs(useBookingStore())

const { regionId } = useRegion()

// fetch api data
const productInfoQueryParams = computed(() => ({
  from: 'Niceday',
  sale_region_id: regionId.value,
  ...(app.geolocation.locatedAt
    ? {
        lat: app.geolocation.latitude,
        lng: app.geolocation.longitude,
      }
    : {}),
}))

const productInfo = computed(() => productStore.productInfo)
const branchProducts = computed(() => productStore.listIds.branchRelatedProduct)

const { error } = await useDualAsyncData(getAsyncDataKey(ASYNC_DATA_KEYS.PRODUCT_INFO, [productId.value]), async () => {
  const { data: productInfoRes } = await getProductInfo(productId.value, productInfoQueryParams.value)
  productStore.setProductInfo(productInfoRes)
  return productInfoRes
})

if (error.value) {
  // @TODO: 目前先一律到 404，未來有需要時再各別分 error code 處理。
  throwNotFoundError()
}

const uiProductInfoPromise = useAsyncData(
  getAsyncDataKey(ASYNC_DATA_KEYS.PRODUCT_STATIC_DATA, [productId.value]),
  async () => {
    const staticDataRequestUrl = productInfo.value?.static_link

    const { data: productDetailRes } = staticDataRequestUrl
      ? await useFetch(staticDataRequestUrl, {
          headers: {
            Platform: 'Niceday',
            'Accept-Language': localeProperties.value.name || DEFAULT_LANG,
          },
        })
      : { data: {} as any }
    const uiProductInfo = {
      ...productInfo.value,
      ...(productDetailRes
        ? {
            product_intro: productDetailRes.value?.data.product_intro,
            purchase_notice: productDetailRes.value.data.purchase_notice,
          }
        : {}),
    } as UiProductInfo

    productStore.setProductInfo(uiProductInfo)
    bookingProduct.value = uiProductInfo
    return uiProductInfo
  },
  { watch: [productId] }
)

const branchProductsPromise = useAsyncData(
  getAsyncDataKey(ASYNC_DATA_KEYS.BRANCH_PRODUCTS, [productInfo.value?.bid || 0]),
  async () => {
    if (!regionId.value) return []

    const { data: branchProducts } = await getProducts({
      filter_regionid: regionId.value,
      filter_bid: `${productInfo.value?.bid || ''}`,
      sortby: SortRuleEnum.NEW_RELEASE,
      size: 9999,
    })

    productStore.handleProducts('branchRelatedProduct', branchProducts)

    return branchProducts
  },
  { server: false }
)
const sameBranchOtherProductIds = computed(() => {
  if (!branchProducts.value) return []

  return branchProducts.value?.filter(id => id !== Number(productId.value))
})

const productCommentsQueryParams = computed(() => ({
  start: 0,
  size: 100,
  sortby: 'latest',
}))
const productCommentsPromise = useAsyncData(
  getAsyncDataKey(ASYNC_DATA_KEYS.PRODUCT_COMMENTS, [productId.value]),
  async () => {
    const result = await getProductComments(productId.value, productCommentsQueryParams.value)
    return result
  },
  { watch: [productId] }
)

const [{ data: productComments, pending: isFetchingProductComments }] = await Promise.all([
  productCommentsPromise,
  uiProductInfoPromise,
  branchProductsPromise,
])

useNicedaySeoSetting('product', { productInfo, productComments })

const isSoldOut = computed(() => productInfo.value?.onboard === 0)

const sections = computed(() => {
  if (!productInfo?.value) return []

  return [
    {
      id: 'product-detail-section-gallery',
      component: ProductSectionGallery,
      show: !!productInfo?.value?.images?.length,
      props: {
        class: '-mb-2 md:mb-0',
        product: productInfo.value,
      },
    },
    {
      id: 'product-detail-section-name',
      component: ProductSectionName,
      show: !app.isDesktopScreen,
      props: {
        product: productInfo.value,
      },
    },
    {
      id: 'product-detail-section-basic-info',
      component: ProductSectionBasicInfo,
      show: !!productInfo.value?.description,
      label: t('product_brief'),
      props: {
        product: productInfo.value,
      },
    },
    {
      id: 'product-detail-section-intro',
      component: ProductSectionIntro,
      show: !!productInfo.value?.product_intro?.length,
      label: t('product_desc'),
      props: {
        product: productInfo.value,
      },
    },
    {
      id: 'product-detail-section-price-list',
      component: ProductSectionPriceList,
      show: !!productInfo.value?.price_list?.length,
      label: t('product_pricing_table'),
      props: {
        product: productInfo.value,
      },
    },
    {
      id: 'product-detail-section-need-to-know',
      component: ProductSectionNeedToKnow,
      show: !!productInfo.value?.purchase_notice?.length,
      label: t('product_purchase_notice'),
      props: {
        product: productInfo.value,
      },
    },
    {
      id: 'product-detail-section-comments',
      component: ProductSectionComments,
      show: !!productComments.value?.data?.comment?.length,
      label: t('product_reviews'),
      props: {
        product: productInfo.value,
        productComments: productComments.value?.data,
        isLoading: isFetchingProductComments.value,
      },
    },
    {
      id: 'product-detail-section-branch-info',
      component: ProductSectionBranchInfo,
      show: !!productInfo.value?.branch_intro,
      label: t('branch_desc'),
      props: {
        product: productInfo.value,
      },
    },
    {
      id: 'product-detail-section-address',
      component: ProductSectionAddress,
      show: !!productInfo.value?.address,
      label: t('all_address'),
      props: {
        product: productInfo.value,
      },
    },
    {
      id: 'product-detail-section-notices',
      component: ProductSectionNotices,
      show:
        !!productInfo.value?.notices ||
        !!productInfo.value?.need_to_know ||
        !!productInfo.value?.cancel_rules ||
        !!productInfo.value?.reschedule_rules,
      label: t('product_notice_items'),
      props: {
        product: productInfo.value,
      },
    },
    {
      id: 'product-detail-section-reservation',
      component: ProductSectionReservation,
      show: !isSoldOut.value && !app.isDesktopScreen,
      label: t('begin_reserve'),
      props: {
        product: productInfo.value,
        bookingItem: bookingItem.value,
        additions: additions.value,
        upgrades: upgrades.value,
      },
      events: {
        'update:bookingItem': (value: UiProductSelectedData[]) => (bookingItem.value = value),
        'update:additions': (value: ProductAdditionsResource[]) => (additions.value = value),
        'update:upgrades': (value: ProductUpgradeResource[]) => (upgrades.value = value),
      },
    },
    {
      id: 'product-detail-section-faq',
      component: ProductSectionFaq,
      show: true,
      label: t('category_faq_title'),
      props: {
        product: productInfo.value,
      },
    },
    ...(featureToggleStore.isRelatedTagsEnabled
      ? [
          {
            id: 'product-detail-section-related-tags',
            component: ProductSectionRelatedTags,
            show: !!productInfo.value?.tags?.length,
            // @FIXME: waiting for PM to update i18n json
            label: '更多相似類別',
            props: {
              product: productInfo.value,
            },
          },
        ]
      : []),
    {
      id: 'product-detail-section-related-products',
      show: !!sameBranchOtherProductIds.value?.length,
      label: t('similar_products'),
    },
  ]
})

// 下滑一段高度後，顯示 section tabs
const { y } = useWindowScroll()
const isSectionTabsShown = computed(() => {
  if (import.meta.server) return false
  return y.value > 256
})

// 處理畫面 scroll 時，調整當前在畫面被 highlight 的 section tab
const activatedTab = ref('product-detail-section-basic-info')
const isAnimating = ref(false)

const topTabsClassName = 'product-detail_top-tab-sections'

provide('isSoldOut', isSoldOut)

onMounted(() => {
  const doms = document.querySelectorAll(`.${topTabsClassName}`)
  useIntersectionObserver(
    Array.from(doms) as MaybeElement[],
    entries => {
      entries.forEach(entry => {
        if (isAnimating.value || !entry.isIntersecting) return
        activatedTab.value = entry.target.id
      })
    },
    { threshold: 0.1, rootMargin: '-144px 0px -144px 0px' }
  )

  useNuxtApp().$trackEvent(GtmEvent.PRODUCT_PAGE_VIEWED)
})

// 處理點擊 tab 時， scroll 到指定元素過程中觸發 Intersection Observer 的事件
const handleTabChange = () => {
  isAnimating.value = true
  debounceResetAnimating()
}
const debounceResetAnimating = useDebounceFn(() => {
  isAnimating.value = false
}, 1500)

onBeforeMount(() => {
  bookingItem.value = []
  additions.value = []
  upgrades.value = []
})
</script>

<style lang="scss">
#product-page {
  min-height: var(--content-min-height);
  .card {
    @apply bg-white md:rounded py-4 px-5;
  }
  .title {
    @apply text-xl font-500 py-3;
  }
}
</style>
